import { Core } from "@faire/design-tokens";
import { IOwFFormFieldSchema } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import { OwFFormSectionFields } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormSectionFields";
import { useIsNMIApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useIsSoftCreditCheckApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsSoftCreditCheckApplication";
import { useOwFForm } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFForm";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC } from "react";

interface IProps {
  title: string;
  optional?: boolean;
  formFields: IOwFFormFieldSchema[];
  viewState: ReturnType<typeof useOwFForm>;
  hideBorders?: boolean;
}

export const OwFFormSection: FC<IProps> = ({
  title,
  optional,
  formFields,
  viewState,
  hideBorders,
}) => {
  const { isNMIApplication } = useIsNMIApplication();
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();

  if (formFields.length === 0) {
    return null;
  }

  if (isNMIApplication) {
    return (
      <Column>
        <Spacer height={20} />
        {title ? <FormSectionTitle title={title} /> : null}
        <Spacer height="2x" />
        <OwFFormSectionFields
          formFields={formFields}
          formState={viewState}
          optional={optional}
        />
      </Column>
    );
  }

  if (isSoftCreditCheckApplication) {
    return (
      <OwFFormSectionFields
        formFields={formFields}
        formState={viewState}
        optional={optional}
      />
    );
  }

  return (
    <Column>
      {hideBorders ? null : (
        <>
          <HorizontalLine />
          <Spacer height={20} />
        </>
      )}
      {title ? <FormSectionTitle title={title} optional={optional} /> : null}
      <Spacer height="2x" />
      <OwFFormSectionFields
        formFields={formFields}
        formState={viewState}
        optional={optional}
      />
    </Column>
  );
};

const FormSectionTitle: React.FC<Pick<IProps, "title" | "optional">> = ({
  title,
  optional,
}) => (
  <Row>
    <Typography variant="subheadingSansMedium">{title}</Typography>
    {optional ? (
      <>
        <Spacer width={4} />
        <Typography variant="subheadingSansMedium" color={Core.text.subdued}>
          <StrictLocalMsg
            defaultMessage="(optional)"
            description={{
              text: "Indicating that a form section is not required for completion",
              img: "https://cdn.faire.com/fastly/327d904b989af889d22ef2470d97c3d2f0d212947581f8444b3f15e515e82054.png",
            }}
          />
        </Typography>
      </>
    ) : null}
  </Row>
);
